import {
  ConflictError,
  RemoteDocumentStore,
  type DocumentRecord,
  type DocumentStore,
  type SaveOptions,
  type StoredDocument,
} from '$lib/services/document-store';
import { UrlPlaceStore, type Place, type PlaceStore, type PlaceView } from '$lib/services/place';
import {
  cleanSettings,
  DEFAULT_SETTINGS,
  DRAWING_FILE_EXTENSION,
  readDrawingFile,
  uniqueName,
  writeDrawingFile,
  sameLayers,
  sameParams,
  sameSettings,
  withBaseLayer,
  type ScriptLayers,
  type ScriptParams,
  type ScriptSettings,
} from '$lib/core/script';
import { slugify } from '$lib/services/download';
import type { EditorState } from './editor-state.svelte';
import { SAMPLE_PARAMS, SAMPLE_SCRIPT } from './sample';

const AUTOSAVE_DELAY_MS = 600;
const UNTITLED = 'Untitled drawing';
/** How long panning and zooming must pause before the URL takes the view. */
export const PLACE_DELAY_MS = 300;
/** How long to wait before asking again for the first drawing after a failed request. */
export const RETRY_DELAY_MS = 1500;

/**
 * Owns the document library: which drawing is open, saving it, and picking up edits made
 * elsewhere to the open drawing. The drawings list is fetched only when asked for. Every save names the revision it was based on; when the server refuses one
 * because Claude saved first, the editor takes Claude's version and says so. A new drawing
 * is not on the server until its first change, which creates it.
 */
export class DocumentSession {
  records = $state<DocumentRecord[]>([]);
  /** Whether the list is being fetched, or the last fetch failed, so neither shows as empty. */
  listState = $state<'ready' | 'loading' | 'failed'>('ready');
  savedAt = $state<number | null>(null);
  /** A one-line message for the user, such as a save that lost to Claude's edit. */
  notice = $state<string | null>(null);

  private timer: ReturnType<typeof setTimeout> | null = null;
  private placeTimer: ReturnType<typeof setTimeout> | null = null;
  /** The revision, name, text, params, layers and settings the editor last loaded or saved. */
  private rev = 0;
  private savedName = '';
  private savedSource = '';
  private savedParams: ScriptParams = [];
  private savedLayers: ScriptLayers = withBaseLayer([]);
  private savedSettings: ScriptSettings = DEFAULT_SETTINGS;
  private saving: Promise<void> | null = null;
  /**
   * Creating, opening and deleting a drawing change which drawing is open, so checks for
   * edits made elsewhere stay out of the way: none starts while one runs, and one already
   * waiting on the server throws away what it read, since `generation` has moved on.
   */
  private generation = 0;
  private changes = 0;
  private checking: Promise<void> | null = null;
  private disposed = false;

  constructor(
    private readonly editor: EditorState,
    private readonly confirm: (message: string, confirmLabel: string) => Promise<boolean>,
    private readonly store: DocumentStore = new RemoteDocumentStore(),
    private readonly place: PlaceStore = new UrlPlaceStore(),
  ) {}

  /** Opens the drawing the URL names, else a new one, never one guessed from the past. */
  async init(): Promise<void> {
    const first = await this.firstDrawing();
    if (first === undefined) return;
    await this.change(async () => {
      if (first) this.show(first.drawing, first.place?.view);
      else this.startNew();
    });
  }

  /** Picks up edits made elsewhere, such as by Claude; the editor calls it on window focus. */
  async checkForChanges(): Promise<void> {
    if (this.checking || this.changes > 0) return;
    this.checking = this.check().finally(() => (this.checking = null));
    await this.checking;
  }

  /**
   * Loads the drawing in the URL, else none, and gives an account with no drawings the
   * sample, in its list; retries while the server cannot answer, as it sometimes cannot
   * just after a reload. Undefined means the session was disposed.
   */
  private async firstDrawing(): Promise<
    { drawing: StoredDocument; place: Place } | null | undefined
  > {
    while (!this.disposed) {
      try {
        const place = this.place.read();
        const placed = place ? await this.store.load(place.drawingId) : null;
        if (place && placed) {
          this.notice = null;
          return { drawing: placed, place };
        }
        if ((await this.store.list()).length === 0) {
          await this.store.create('Parametric plate', SAMPLE_SCRIPT, SAMPLE_PARAMS);
        }
        this.notice = null;
        return null;
      } catch {
        this.notice = 'Cannot reach your drawings; trying again';
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      }
    }
    return undefined;
  }

  /** Fetches the drawings list; nothing lists drawings unless someone asks to see them. */
  async refresh(): Promise<void> {
    this.listState = 'loading';
    try {
      this.records = await this.store.list();
      this.listState = 'ready';
    } catch {
      this.listState = 'failed';
    }
  }

  async create(
    name = UNTITLED,
    source = '',
    params: ScriptParams = [],
    layers: ScriptLayers = [],
    settings?: ScriptSettings,
  ): Promise<void> {
    await this.change(async () => {
      await this.flush();
      const drawing = await this.store.create(name, source, params, layers, settings);
      await this.refresh();
      this.show(drawing);
    });
  }

  async open(id: string): Promise<void> {
    await this.change(async () => {
      await this.flush();
      const drawing = await this.store.load(id);
      if (drawing) this.show(drawing);
    });
  }

  async duplicate(id: string): Promise<void> {
    await this.flush();
    const drawing = await this.store.load(id);
    if (!drawing) return;
    await this.create(
      `${drawing.name} copy`,
      drawing.source,
      drawing.params,
      drawing.layers,
      drawing.settings,
    );
  }

  /** A drawing as a file to download, with any edit to it not yet saved. */
  async fileOf(id: string): Promise<{ name: string; text: string } | null> {
    await this.flush();
    const drawing = await this.store.load(id);
    if (!drawing) return null;
    const text = writeDrawingFile({
      name: drawing.name,
      source: drawing.source,
      params: drawing.params ?? [],
      layers: withBaseLayer(drawing.layers ?? []),
      settings: cleanSettings(drawing.settings),
    });
    return { name: `${slugify(drawing.name)}.${DRAWING_FILE_EXTENSION}`, text };
  }

  /**
   * Makes a new drawing from a file and opens it, never replacing one; a name already taken
   * is numbered. A file it cannot read creates nothing, and the notice says why.
   */
  async importFile(text: string): Promise<void> {
    const read = readDrawingFile(text);
    if (!read.ok) {
      this.notice = read.problem;
      return;
    }
    const { name, source, params, layers, settings } = read.drawing;
    const taken = (await this.store.list()).map((record) => record.name);
    await this.create(uniqueName(name, taken), source, params, layers, settings);
  }

  async remove(id: string): Promise<void> {
    const record = this.records.find((entry) => entry.id === id);
    if (!record) return;
    if (!(await this.confirm(`Delete "${record.name}"? This cannot be undone.`, 'Delete'))) return;
    await this.change(async () => {
      if (id === this.editor.documentId) {
        // Its unsaved edit has nowhere to go once it is gone, so it is dropped, not saved.
        if (this.timer) clearTimeout(this.timer);
        this.timer = null;
        await this.saving;
      }
      await this.store.remove(id);
      await this.refresh();
      if (id === this.editor.documentId) this.startNew();
    });
  }

  /** Runs a change to which drawing is open, after any check already under way gives up. */
  private async change(run: () => Promise<void>): Promise<void> {
    this.generation += 1;
    this.changes += 1;
    try {
      await this.checking;
      await run();
    } finally {
      this.changes -= 1;
    }
  }

  async rename(name: string): Promise<void> {
    this.editor.script.rename(name);
    await this.saveNow();
  }

  /** Debounced; the editor calls this whenever the script, a param or a layer changes. */
  scheduleSave(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.saveNow(), AUTOSAVE_DELAY_MS);
  }

  /** Writes any pending change immediately, e.g. before the page goes away. */
  async flush(): Promise<void> {
    if (this.timer) await this.saveNow();
    else await this.saving;
  }

  /** Saves as the page goes away, so the browser finishes the save after it has gone. */
  leave(): Promise<void> {
    return this.saveNow({ leaving: true });
  }

  async saveNow(options: SaveOptions = {}): Promise<void> {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    // Saves run one after another, so each is based on the revision the last one produced.
    const queued = (this.saving ?? Promise.resolve()).then(() => this.write(options));
    this.saving = queued;
    await queued;
    if (this.saving === queued) this.saving = null;
  }

  dispose(): void {
    this.disposed = true;
    if (this.placeTimer) clearTimeout(this.placeTimer);
    void this.flush();
  }

  private async write(options: SaveOptions): Promise<void> {
    const id = this.editor.documentId;
    const { name, source, params, layers, settings } = this.editor.script;
    if (!this.unsaved() && name === this.savedName) return;
    try {
      // Opening another drawing waits for this write, so a new one is still the open one here.
      const saved = id
        ? await this.store.save(id, { name, source, params, layers, settings }, this.rev, options)
        : await this.store.create(name, source, params, layers, settings, options);
      this.remember(saved);
      if (!id) {
        this.editor.documentId = saved.id;
        this.place.write({ drawingId: saved.id });
      }
      this.notice = null;
    } catch (error) {
      if (!(error instanceof ConflictError)) throw error;
      this.notice = `${error.message}, probably by Claude; showing the saved version`;
      this.take(error.current);
    }
  }

  /**
   * Picks up a newer revision of the open drawing saved elsewhere, after saving what is
   * pending, since nothing checks again until the window next gains focus.
   */
  private async check(): Promise<void> {
    const started = this.generation;
    await this.flush();
    const id = this.editor.documentId;
    if (this.generation !== started || !id || this.editor.typing || this.unsaved()) return;
    const drawing = await this.store.load(id);
    if (this.generation !== started) return;
    if (drawing && drawing.id === this.editor.documentId && drawing.rev > this.rev) {
      this.take(drawing);
    }
  }

  private unsaved(): boolean {
    const { source, params, layers, settings } = this.editor.script;
    return (
      source !== this.savedSource ||
      !sameParams(params, this.savedParams) ||
      !sameLayers(layers, this.savedLayers) ||
      !sameSettings(settings, this.savedSettings)
    );
  }

  /** Puts the view in the URL once panning and zooming pause, and once the view is fitted. */
  notePlace({ center, zoom }: PlaceView): void {
    const drawingId = this.editor.documentId;
    if (!drawingId || this.editor.needsFit) return;
    if (this.placeTimer) clearTimeout(this.placeTimer);
    this.placeTimer = setTimeout(() => {
      this.placeTimer = null;
      if (this.editor.documentId === drawingId)
        this.place.write({ drawingId, view: { center, zoom } });
    }, PLACE_DELAY_MS);
  }

  /** What the server holds for the open drawing, so only changes since are saved. */
  private remember(drawing: StoredDocument | null): void {
    this.rev = drawing?.rev ?? 0;
    this.savedName = drawing?.name ?? UNTITLED;
    this.savedSource = drawing?.source ?? '';
    this.savedParams = drawing?.params ?? [];
    this.savedLayers = withBaseLayer(drawing?.layers ?? []);
    this.savedSettings = cleanSettings(drawing?.settings);
    this.savedAt = drawing?.updatedAt ?? null;
  }

  private show(drawing: StoredDocument, view?: PlaceView): void {
    this.remember(drawing);
    this.notice = null;
    this.editor.loadScript(
      drawing.name,
      drawing.source,
      drawing.id,
      this.savedParams,
      this.savedLayers,
      this.savedSettings,
      view,
    );
    if (this.placeTimer) clearTimeout(this.placeTimer);
    this.placeTimer = null;
    this.place.write({ drawingId: drawing.id, ...(view ? { view } : {}) });
  }

  /** Shows a new, empty drawing, which its first change creates on the server. */
  private startNew(): void {
    this.remember(null);
    this.notice = null;
    this.editor.loadScript(
      this.savedName,
      this.savedSource,
      null,
      this.savedParams,
      this.savedLayers,
      this.savedSettings,
    );
    if (this.placeTimer) clearTimeout(this.placeTimer);
    this.placeTimer = null;
    this.place.write(null);
  }

  /** Shows a newer version of the open drawing without refitting the view. */
  private take(drawing: StoredDocument): void {
    this.remember(drawing);
    this.editor.applyRemote(
      drawing.name,
      drawing.source,
      this.savedParams,
      this.savedLayers,
      this.savedSettings,
    );
  }
}
